'use client';

import { createContext, useContext, useState, useEffect } from 'react';
import { Sector, GroupSector } from '@/types';
import sectorService from '@/templates/Starglobal3D/utils/services/sector';

interface SectorContextType {
    sectors: Sector[];
    groupSectors: GroupSector[];
    isLoading: boolean;
    error: string | null;
}

const SectorContext = createContext<SectorContextType | undefined>(undefined);

export const useSector = () => {
    const context = useContext(SectorContext);
    if (!context) {
        throw new Error('useSector must be used within SectorProvider');
    }
    return context;
};

export const SectorProvider = ({ children }: { children: React.ReactNode }) => {
    const [sectors, setSectors] = useState<Sector[]>([]);
    const [groupSectors, setGroupSectors] = useState<GroupSector[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchSectors = async () => {
            try {
                setIsLoading(true);
                const data = await sectorService.getAllNestedSectors();
                if (data?.data) {
                    const groups = data.data as GroupSector[];
                    setGroupSectors(groups);
                    const flatSectors = groups.flatMap((group) => group.fields);
                    setSectors(flatSectors.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));
                }
            } catch (err) {
                console.error('Error fetching sectors:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch sectors');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSectors();
    }, []);

    return (
        <SectorContext.Provider value={{ sectors, groupSectors, isLoading, error }}>
            {children}
        </SectorContext.Provider>
    );
};